import type { Role } from '@/session.tsx';

/**
 * The group map, as an ordered list of rows rather than the object the API takes.
 *
 * An object cannot express a half-typed row — `{ '': 'viewer' }` collides with the next blank one
 * — and it has no order, so editing would reshuffle under the cursor. The form owns rows; the
 * boundary converts.
 */
export interface GroupRow {
  group: string;
  role: Role;
}

export const ROLE_OPTIONS: readonly Role[] = ['viewer', 'editor', 'admin', 'owner'];

export function rowsFromMap(map: Record<string, Role>): GroupRow[] {
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, role]) => ({ group, role }));
}

/** Drops blank rows, so a half-finished one does not become a mapping. */
export function mapFromRows(rows: readonly GroupRow[]): Record<string, Role> {
  const map: Record<string, Role> = {};
  for (const row of rows) {
    const group = row.group.trim();
    if (group) map[group] = row.role;
  }
  return map;
}

export interface GroupMapProblem {
  index: number;
  message: string;
}

/**
 * What is wrong with the rows, before the control plane is asked.
 *
 * The server validates independently — this exists to answer sooner. The duplicate check is the
 * one that matters: two rows naming the same group silently collapse into one mapping on save,
 * and whichever the operator typed second is the one that survives.
 */
export function checkGroupRows(rows: readonly GroupRow[]): GroupMapProblem[] {
  const problems: GroupMapProblem[] = [];
  const seen = new Map<string, number>();

  rows.forEach((row, index) => {
    const group = row.group.trim();
    if (!group) {
      // Only complain about a blank row if it is not the last one — a trailing blank is someone
      // who has just clicked "add" and has not started typing.
      if (index !== rows.length - 1) {
        problems.push({ index, message: 'A group name is required, or remove the row.' });
      }
      return;
    }
    if (seen.has(group)) {
      problems.push({
        index,
        message: `"${group}" is already mapped in row ${seen.get(group)! + 1}. Only one of them would take effect.`,
      });
      return;
    }
    seen.set(group, index);
  });

  return problems;
}

/**
 * Whether saving these rows would lock the caller out of their own organization.
 *
 * Not a validation error — a tenant may legitimately manage members by hand — but the one
 * outcome here that an operator cannot undo through this console, since fixing it needs an
 * account they can no longer sign in with.
 */
export function warnsAboutLockout(rows: readonly GroupRow[]): boolean {
  const map = mapFromRows(rows);
  return Object.values(map).every((role) => role !== 'owner' && role !== 'admin');
}
